import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { portalApi } from '../api/endpoints';
import { resolveApiAssetUrl } from '../api/client';
import { DEFAULT_BRAND_COLOR } from '../theme';
import type { PortalBranding } from '../types';

interface PortalContextValue {
  /** null = không có cổng riêng (domain gốc) hoặc chưa tải xong. */
  branding: PortalBranding | null;
  loading: boolean;
}

const PortalContext = createContext<PortalContextValue>({ branding: null, loading: true });

export const DEFAULT_TITLE = 'Alliance ITSC Acceptance Portal';

/** Chữ cái đại diện trên favicon: ký tự đầu tiên của tên hiển thị (khác avatar TopBar — nơi đó lấy chữ cuối vì đó là tên người). */
function firstLetter(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?';
}

/**
 * Favicon dạng vòng tròn màu thương hiệu + chữ cái đầu tên hiển thị, vẽ bằng
 * SVG nhúng thẳng vào data URI — không cần dựng/host file ảnh riêng cho mỗi
 * cổng. Browser tự cache theo nội dung href, đổi cổng là đổi favicon ngay.
 */
function letterFaviconDataUrl(letter: string, bgColor: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">`
    + `<circle cx="32" cy="32" r="32" fill="${bgColor}"/>`
    + `<text x="32" y="32" dy="0.35em" text-anchor="middle" `
    + `font-family="Arial, Helvetica, sans-serif" font-size="34" font-weight="700" `
    + `fill="#ffffff">${letter}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Gắn/đổi thẻ <link rel="icon"> trong <head> — tạo mới nếu trang chưa có sẵn. */
function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

const FAVICON_SIZE = 64;

/**
 * Vẽ lại logo thành favicon hình tròn (cắt theo kiểu `object-fit: cover` —
 * logo không vuông sẽ bị cắt bớt cạnh dài để lấp đầy vòng tròn, đồng nhất
 * dáng với favicon chữ-cái dự phòng). Trả `null` nếu ảnh tải lỗi hoặc canvas
 * bị "nhiễm bẩn" (thiếu CORS) — khi đó gọi nơi dùng tự rơi về favicon chữ cái.
 */
function cropLogoToCircleDataUrl(logoUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = FAVICON_SIZE;
        canvas.height = FAVICON_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);

        ctx.beginPath();
        ctx.arc(FAVICON_SIZE / 2, FAVICON_SIZE / 2, FAVICON_SIZE / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        // object-fit: cover — scale theo cạnh ngắn hơn rồi căn giữa, cạnh dài bị cắt.
        const scale = Math.max(FAVICON_SIZE / img.width, FAVICON_SIZE / img.height);
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        ctx.drawImage(
          img,
          (FAVICON_SIZE - drawWidth) / 2,
          (FAVICON_SIZE - drawHeight) / 2,
          drawWidth,
          drawHeight,
        );

        resolve(canvas.toDataURL('image/png'));
      } catch {
        // SecurityError khi canvas bị nhiễm bẩn (ảnh thiếu CORS) — rơi về favicon chữ cái.
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = logoUrl;
  });
}

/**
 * Favicon của cổng, theo thứ tự ưu tiên:
 * 1. `faviconUrl` — ảnh favicon riêng admin đã tải lên (vuông, tối ưu sẵn cho
 *    tab trình duyệt) — DÙNG THẲNG, không cắt gì cả: đây là lựa chọn chủ ý
 *    của admin, ép cắt tròn lên ảnh họ đã tự thiết kế là không đúng.
 * 2. `logoUrl` — chưa có favicon riêng thì tạm cắt tròn logo thương hiệu
 *    (thường chữ nhật ngang, dùng cho trang đăng nhập/TopBar) làm dự phòng.
 * 3. Không có ảnh nào (hoặc tải/cắt lỗi) — vòng tròn màu thương hiệu + chữ
 *    cái đầu tên hiển thị, tự vẽ bằng SVG.
 */
async function faviconHrefFor(
  name: string,
  faviconUrl: string | null,
  logoUrl: string | null,
  bgColor: string,
): Promise<string> {
  const resolvedFavicon = resolveApiAssetUrl(faviconUrl);
  if (resolvedFavicon) return resolvedFavicon;

  const resolvedLogo = resolveApiAssetUrl(logoUrl);
  if (resolvedLogo) {
    const cropped = await cropLogoToCircleDataUrl(resolvedLogo);
    if (cropped) return cropped;
  }
  return letterFaviconDataUrl(firstLetter(name), bgColor);
}

/**
 * Thương hiệu theo domain truy cập (GET /api/portal) — tải MỘT LẦN lúc khởi
 * động app, KHÔNG phụ thuộc đăng nhập, vì trang Login cũng cần hiện đúng tên/
 * logo trước khi ai đó đăng nhập.
 */
export function PortalProvider({ children }: { children: ReactNode }): JSX.Element {
  const [branding, setBranding] = useState<PortalBranding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void portalApi
      .getPublicBranding()
      .then((result) => {
        if (!active) return;
        setBranding(result);
        const title = result?.display_name ?? DEFAULT_TITLE;
        document.title = title;
        void faviconHrefFor(
          title,
          result?.favicon_url ?? null,
          result?.logo_url ?? null,
          result?.brand_color || DEFAULT_BRAND_COLOR,
        ).then((href) => {
          if (active) setFavicon(href);
        });
      })
      .catch(() => {
        // Domain gốc / lỗi mạng tạm thời → coi như không có cổng riêng.
        if (!active) return;
        setBranding(null);
        document.title = DEFAULT_TITLE;
        void faviconHrefFor(DEFAULT_TITLE, null, null, DEFAULT_BRAND_COLOR).then((href) => {
          if (active) setFavicon(href);
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return <PortalContext.Provider value={{ branding, loading }}>{children}</PortalContext.Provider>;
}

export function usePortalBranding(): PortalContextValue {
  return useContext(PortalContext);
}
